import React, { useContext } from "react";
import { format } from "date-fns";
import styled from "styles/styled_components";

import { BORDERS } from "components/schedule/time_block";
import {
  AddShiftButton,
  ShiftButton,
  UnavailButton,
} from "containers/schedule/block_button_containers";

import PersonContext from "helpers/context/person";
import { filterBlocksByDate } from "helpers/schedule/shifts";
import { IShift, IPosUser, IUnavailabilityBlock } from "types/schedules";

const StyledShiftCell = styled.td`
  border: ${(props) => props.theme.lightBorder};
  cursor: pointer;
  vertical-align: top;
  height: inherit;
`;

interface IDayCell {
  date: Date;
  interactiveView: boolean;
}

function DayCell({ date, interactiveView }: IDayCell) {
  const person = useContext(PersonContext);
  const { assignee, shifts, unavail } = person;

  let unavailButtons;
  let addShiftButton;

  // Find Shifts and Unavailability for this day
  const todaysShifts = filterBlocksByDate(date, shifts) as IShift[];
  const shiftButtons = todaysShifts.map((shift, idx) => {
    const shiftIdentifier: string = `${shift.role.name}-${format(
      shift.startTime,
      "MMDDYY"
    )}-${idx}`;

    const userId: string = assignee.match({
      Just: (employee: IPosUser) => employee.uuid,
      Nothing: () => "unassigned",
    });

    return (
      <ShiftButton
        key={`${userId}-${shiftIdentifier}`}
        border={
          assignee.isNothing() ? BORDERS.UNASSIGNED_WEEK : BORDERS.SHIFT_WEEK
        }
        date={date}
        shift={shift}
        interactiveView={interactiveView}
        person={person}
        partialFlag={true}
      />
    );
  });

  // Only when in interactiveView, display UnavailButtons or AddShiftButton
  // Display TimeBlockContainer when it has been opened
  if (interactiveView) {
    // If the person has unavailability, display it
    const todaysUnavails = unavail.match({
      Just: (unavails: IUnavailabilityBlock[]) =>
        filterBlocksByDate(date, unavails) as IUnavailabilityBlock[],
      Nothing: () => [],
    });

    if (todaysUnavails.length) {
      unavailButtons = todaysUnavails.map((unavailBlock) => (
        <UnavailButton
          key={unavailBlock.uuid}
          border={BORDERS.NONE}
          date={date}
          person={person}
          unavail={unavailBlock}
        />
      ));
    } else if (shiftButtons.length === 0 || assignee.isNothing()) {
      // Display an AddShiftButton when person has no shifts, or we're in Unassigned row
      addShiftButton = (
        <AddShiftButton
          date={date}
          numShifts={todaysShifts.length}
          person={person}
        />
      );
    }
  }

  return (
    <StyledShiftCell>
      {shiftButtons}
      {unavailButtons}
      {addShiftButton}
    </StyledShiftCell>
  );
}

export default DayCell;
